import { useState } from 'react';

import { requestApi } from 'api/Api';

import {
  CHUNK_SIZE,
  FILE_UPLOAD_STATUS
} from 'components/admin/pages/GeneralUploader/components/lib/constants';
import { showAlert } from 'components/shared/Alert/Alert';

const useFileUploader = () => {
  const [uploading, setUploading] = useState(false);

  const uploadFile = async (files, selectedPractice, selectedVendor, setUploadProgressList) => {
    if (files.length === 0) return;
    setUploading(true);

    const response = await requestApi({
      url: '/api/admin/uploader/initiate-upload',
      params: {
        practiceId: selectedPractice.id,
        vendorId: selectedVendor.id
      },
      onError: (error) => {
        showAlert({
          title: error ?? `Something went wrong to upload!`,
          color: 'danger'
        });
        setUploading(false);
      }
    });

    if (!response?.importInfoId) return;

    const { importInfoId } = response;

    for (const file of files) {
      const totalParts = Math.ceil(file.size / CHUNK_SIZE);

      const { info } = await requestApi({
        url: '/api/admin/uploader/initiate-multipart',
        params: {
          filename: file.name,
          contentType: file.type,
          practiceId: selectedPractice.id,
          vendorId: selectedVendor.id
        },
        onError: (error) => {
          showAlert({
            title: error ?? `Something went wrong to upload!`,
            color: 'danger'
          });
          updateFileStatus(file.name, FILE_UPLOAD_STATUS.ERROR, setUploadProgressList);
        }
      });

      if (info) {
        const { uploadId, key } = info;
        const parts = [];

        for (let partNumber = 1; partNumber <= totalParts; partNumber++) {
          const start = (partNumber - 1) * CHUNK_SIZE;
          const end = Math.min(start + CHUNK_SIZE, file.size);
          const blob = file.slice(start, end);

          const { signedUrl } = await requestApi({
            url: '/api/admin/uploader/presign-part',
            params: {
              key,
              uploadId,
              partNumber,
              importInfoId
            },
            onError: (error) => {
              showAlert({
                title: error ?? `Something went wrong to upload!`,
                color: 'danger'
              });
              updateFileStatus(file.name, FILE_UPLOAD_STATUS.ERROR, setUploadProgressList);
            }
          });

          if (!signedUrl) break;

          const response = await requestApi({
            url: signedUrl,
            method: 'PUT',
            params: blob,
            returnRaw: true
          });

          if (response.status !== 200) {
            updateFileStatus(file.name, FILE_UPLOAD_STATUS.ERROR, setUploadProgressList);
            break;
          } else {
            updateFileProgress(
              file.name,
              partNumber,
              FILE_UPLOAD_STATUS.UPLOADING,
              setUploadProgressList
            );
          }

          const etag = response.headers.get('ETag')?.replace(/"/g, '');
          if (etag) {
            parts.push({ ETag: etag, PartNumber: partNumber });
          } else {
            throw new Error(`ETag not found for part ${partNumber}`);
          }
        }

        await requestApi({
          url: '/api/admin/uploader/complete-upload',
          params: { key, uploadId, parts, importInfoId, filename: file.name, fileType: file.type },
          onSuccess: () => {
            updateFileStatus(file.name, FILE_UPLOAD_STATUS.UPLOADED, setUploadProgressList);
            showAlert({
              title: `${file.name} uploaded successfully`,
              color: 'success'
            });
          },
          onError: (error) => {
            showAlert({
              title: error ?? `Something went wrong to upload!`,
              color: 'danger'
            });
            updateFileStatus(file.name, FILE_UPLOAD_STATUS.ERROR, setUploadProgressList);
          }
        });
      }
    }
    setUploading(false);
  };

  const updateFileStatus = (filename, status, setUploadProgressList) => {
    setUploadProgressList((prev) =>
      prev.map((p) => (p.filename === filename ? { ...p, status } : p))
    );
  };

  const updateFileProgress = (filename, progress, status, setUploadProgressList) => {
    setUploadProgressList((prev) =>
      prev.map((p) => (p.filename === filename ? { ...p, progress, status } : p))
    );
  };

  return { uploading, uploadFile };
};

export default useFileUploader;
